import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Descriptions, Drawer, Form, Result, Space, Spin, Typography, message } from 'antd';
import { EditOutlined, ReloadOutlined, SwapOutlined, UserSwitchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  Pestania,
  editarCompra,
  editarVenta,
  leerCompra,
  leerVenta,
} from '../../api/oportunidadesApi';
import type { Catalogos, CompraDetalle, EmpleadoUsuario, VentaDetalle } from '../../api/oportunidadesApi';
import { EstadoOportunidadTag, PendienteAsignarTag, ResponsableOportunidad } from './EstadoOportunidadTag';
import { FormularioCompra, FormularioVenta } from './Formularios';
import { AVISO_SIN_FICHA, errorOportunidad } from './errores';
import { NOMBRE_PESTANIA, etiquetaOperacion, servicioAplica } from './reglas';
import { FctAviso, fmtFecha, fmtFechaHora } from '../../fct/react';
import { fct } from '../../fct/tokens/tokens';

const { Paragraph, Text } = Typography;

interface DetalleDrawerProps {
  objetivo: { pestania: Pestania; id: number } | null;
  catalogos: Catalogos;
  yo: EmpleadoUsuario | null;
  onCerrar: () => void;
  onAsignar: (pestania: Pestania, id: number, idResponsable?: number) => void;
  onCambiarEstado: (pestania: Pestania, id: number, idEstado: number) => void;
  /** Tras guardar una edición: refrescar el listado. */
  onCambiado: () => void;
  /** Cambia para forzar la recarga del detalle (tras asignar o cambiar de estado). */
  version: number;
}

const fecha = (iso?: string, conHora = false) =>
  iso ? <span className="fct-mono">{conHora ? fmtFechaHora(iso) : fmtFecha(iso)}</span> : <Text type="secondary">—</Text>;

const texto = (valor?: string | null) => (valor ? valor : <Text type="secondary">—</Text>);

/**
 * Detalle y edición de una oportunidad (R-13). Solo lectura como dato (manual, regla 8): nº,
 * referencia CCL y generador no se editan; estado y responsable se cambian con sus acciones.
 * PAC y aviso EPC de la venta no se muestran (otro ámbito).
 */
export const DetalleDrawer: React.FC<DetalleDrawerProps> = ({
  objetivo, catalogos, yo, onCerrar, onAsignar, onCambiarEstado, onCambiado, version,
}) => {
  const [form] = Form.useForm();
  const [venta, setVenta] = useState<VentaDetalle | null>(null);
  const [compra, setCompra] = useState<CompraDetalle | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editando, setEditando] = useState(false);
  const [guardando, setGuardando] = useState(false);

  // Solo vale la última lectura: una respuesta tardía de otra oportunidad no puede pintarse (ni
  // editarse) bajo el título de esta (revisión independiente, hallazgo 2).
  const ultimaLectura = useRef(0);

  const cargar = useCallback(async () => {
    const n = ++ultimaLectura.current;
    setVenta(null);
    setCompra(null);
    if (!objetivo) return;
    setCargando(true);
    setError(null);
    try {
      if (objetivo.pestania === Pestania.Venta) {
        const v = await leerVenta(objetivo.id);
        if (n === ultimaLectura.current) setVenta(v);
      } else {
        const c = await leerCompra(objetivo.id);
        if (n === ultimaLectura.current) setCompra(c);
      }
    } catch (e) {
      if (n === ultimaLectura.current) setError(errorOportunidad(e).mensaje);
    } finally {
      if (n === ultimaLectura.current) setCargando(false);
    }
  }, [objetivo]);

  useEffect(() => {
    setEditando(false);
    cargar();
  }, [cargar, version]);

  const empezarEdicion = () => {
    if (venta) {
      form.setFieldsValue({
        idFarmacia: venta.idFarmacia,
        idsTitulares: venta.titulares.map((t) => t.idCliente),
        idTipoOperacion: venta.idTipoOperacion,
        idTipoServicio: venta.idTipoServicio,
        idFormaContacto: venta.idFormaContacto,
        idIniciativa: venta.idIniciativa,
        idOrigen: venta.idOrigen,
        // Un detalle migrado de otro origen no se da por bueno: hay que elegirlo (R-09).
        idDetalle: catalogos.detalles.some((d) => d.id === venta.idDetalle && d.idPadre === venta.idOrigen) ? venta.idDetalle : undefined,
        idEpcEntidad: venta.idEpcEntidad,
        idEpcPersona: venta.idEpcPersona,
        fechaGeneracion: dayjs(venta.fechaGeneracion),
        fechaObjetivoCaptacion: venta.fechaObjetivoCaptacion ? dayjs(venta.fechaObjetivoCaptacion) : undefined,
        observaciones: venta.observaciones,
      });
    } else if (compra) {
      form.setFieldsValue({
        idCliente: compra.idCliente,
        idsOperaciones: compra.operaciones.map((o) => o.idOperacion),
        idIniciativa: compra.idIniciativa,
        idOrigen: compra.idOrigen,
        idDetalle: catalogos.detalles.some((d) => d.id === compra.idDetalle && d.idPadre === compra.idOrigen) ? compra.idDetalle : undefined,
        idEpcEntidad: compra.idEpcEntidad,
        idEpcPersona: compra.idEpcPersona,
        fechaSolicitud: dayjs(compra.fechaSolicitud),
        observaciones: compra.observaciones,
      });
    }
    setEditando(true);
  };

  const guardar = async () => {
    const v = await form.validateFields().catch(() => null);
    const actual = venta ?? compra;
    if (!v || !objetivo || !actual || actual.idOportunidad !== objetivo.id) return;
    // Fechas sin tocar: se devuelven tal cual (no se reescribe la fila por un cambio de formato).
    const mismaFecha = (original: string, elegida: dayjs.Dayjs) => (dayjs(original).isSame(elegida) ? original : elegida.format());
    setGuardando(true);
    try {
      if (venta) {
        await editarVenta(venta.idOportunidad, {
          ...v,
          idTipoServicio: servicioAplica(v.idTipoOperacion, catalogos.idTipoOperacionGestion) ? v.idTipoServicio : undefined,
          firma: venta.firma,
          fechaGeneracion: mismaFecha(venta.fechaGeneracion, v.fechaGeneracion),
          fechaObjetivoCaptacion: v.fechaObjetivoCaptacion ? v.fechaObjetivoCaptacion.format('YYYY-MM-DD') : undefined,
          observaciones: v.observaciones?.trim(),
        });
      } else if (compra) {
        await editarCompra(compra.idOportunidad, {
          ...v,
          firma: compra.firma,
          fechaSolicitud: mismaFecha(compra.fechaSolicitud, v.fechaSolicitud),
          idsOperaciones: compra.pestania === Pestania.Solicitud ? v.idsOperaciones : undefined,
          observaciones: v.observaciones?.trim(),
        });
      }
      message.success('Cambios guardados.');
      setEditando(false);
      await cargar();
      onCambiado();
    } catch (e) {
      const err = errorOportunidad(e);
      if (err.conflicto) {
        message.warning('La oportunidad ha cambiado desde que la abriste. Se han cargado los datos actuales: revisa y vuelve a editar.');
        setEditando(false);
        await cargar();
      } else if (err.faltas?.length) {
        message.error(`${err.mensaje} ${err.faltas.join(' ')}`);
      } else {
        message.error(err.mensaje);
      }
    } finally {
      setGuardando(false);
    }
  };

  const d = venta ?? compra;
  const titulo = objetivo
    ? `${objetivo.pestania === Pestania.Solicitud ? 'Solicitud de información' : `Oportunidad de ${NOMBRE_PESTANIA[objetivo.pestania].toLowerCase()}`} nº ${objetivo.id}`
    : '';

  return (
    <Drawer
      open={!!objetivo}
      onClose={onCerrar}
      width={760}
      title={titulo}
      destroyOnClose
      extra={d && !editando && (
        <Space>
          <Button icon={<UserSwitchOutlined />} onClick={() => onAsignar(objetivo!.pestania, objetivo!.id, d.idResponsable)}>Asignar</Button>
          <Button icon={<SwapOutlined />} onClick={() => onCambiarEstado(objetivo!.pestania, objetivo!.id, d.idEstado)}>Cambiar estado</Button>
          <Button icon={<EditOutlined />} onClick={empezarEdicion} disabled={!yo}>Editar</Button>
        </Space>
      )}
      footer={editando && (
        <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={() => setEditando(false)}>Cancelar</Button>
          <Button type="primary" loading={guardando} onClick={guardar}>Guardar cambios</Button>
        </Space>
      )}
    >
      {cargando && <Spin />}
      {error && (
        <Result status="warning" title={error} extra={<Button icon={<ReloadOutlined />} onClick={cargar}>Reintentar</Button>} />
      )}
      {!yo && d && <div style={{ marginBottom: 16 }}><FctAviso tipo="info" titulo={AVISO_SIN_FICHA} /></div>}

      {d && !editando && !cargando && (
        <>
          <Space style={{ marginBottom: 16 }} wrap>
            <EstadoOportunidadTag estado={d.idEstado} />
            {d.pendienteAsignar && <PendienteAsignarTag />}
          </Space>
          <Descriptions column={2} size="small" labelStyle={{ color: fct.tinta['700'] }}>
            {venta && (
              <>
                {/* Quién la generó, junto a la fecha (ajuste de OPOR-T05) */}
                <Descriptions.Item label="Fecha de generación">{fecha(venta.fechaGeneracion, true)}</Descriptions.Item>
                <Descriptions.Item label="Generada por">{texto(venta.generador)}</Descriptions.Item>
                <Descriptions.Item label="Referencia CCL"><span className="fct-mono">{venta.referenciaCcl || '—'}</span></Descriptions.Item>
                <Descriptions.Item label="Objetivo de captación">{fecha(venta.fechaObjetivoCaptacion)}</Descriptions.Item>
                <Descriptions.Item label="Farmacia" span={2}>
                  {venta.idFarmacia ? venta.farmacia : <Text type="secondary">Sin identificar</Text>}
                </Descriptions.Item>
                <Descriptions.Item label="Titulares" span={2}>
                  {venta.titulares.length ? venta.titulares.map((t) => t.nombre).join('; ') : <Text type="secondary">—</Text>}
                </Descriptions.Item>
                <Descriptions.Item label="Tipo de operación">{texto(catalogos.tiposOperacion.find((t) => t.id === venta.idTipoOperacion)?.descripcion)}</Descriptions.Item>
                {(servicioAplica(venta.idTipoOperacion, catalogos.idTipoOperacionGestion) || venta.idTipoServicio) && (
                  <Descriptions.Item label="Servicio">{texto(catalogos.tiposServicio.find((t) => t.id === venta.idTipoServicio)?.descripcion)}</Descriptions.Item>
                )}
                <Descriptions.Item label="Forma de contacto">{texto(catalogos.formasContacto.find((t) => t.id === venta.idFormaContacto)?.descripcion)}</Descriptions.Item>
              </>
            )}
            {compra && (
              <>
                <Descriptions.Item label="Fecha de solicitud">{fecha(compra.fechaSolicitud, true)}</Descriptions.Item>
                {/* Quién la generó, junto a la fecha, como en venta (spec rev. 5, R-20); «—» en las migradas */}
                <Descriptions.Item label="Generada por">{texto(compra.generador)}</Descriptions.Item>
                <Descriptions.Item label="Cliente">
                  {compra.cliente} {compra.codigoCliente && <span className="fct-mono" style={{ color: fct.tinta['700'] }}>· {compra.codigoCliente}</span>}
                </Descriptions.Item>
                {compra.pestania === Pestania.Solicitud && (
                  <Descriptions.Item label="Operaciones" span={2}>
                    {compra.operaciones.length
                      ? compra.operaciones.map((o) => (
                        <div key={o.idOperacion}>
                          <span className="fct-mono">{etiquetaOperacion(o)}</span>{o.fase ? ` · ${o.fase}` : ''}
                        </div>
                      ))
                      : <Text type="secondary">—</Text>}
                  </Descriptions.Item>
                )}
              </>
            )}
            <Descriptions.Item label="Iniciativa">{texto(d.iniciativa)}</Descriptions.Item>
            <Descriptions.Item label="Origen">{texto(d.origen)}</Descriptions.Item>
            <Descriptions.Item label={d.idOrigen === catalogos.idOrigenEpc ? 'EPC' : 'Detalle'} span={2}>{texto(d.detalle)}</Descriptions.Item>
            <Descriptions.Item label="Responsable" span={2}>
              {d.pendienteAsignar
                ? texto(undefined) // la marca «Pendiente de asignar» ya va junto al estado
                : <ResponsableOportunidad responsable={d.responsable} pendienteAsignar={false} responsableBaja={d.responsableBaja} />}
            </Descriptions.Item>
            {d.motivoDescarte && <Descriptions.Item label="Motivo del descarte" span={2}>{d.motivoDescarte}</Descriptions.Item>}
          </Descriptions>
          <Paragraph style={{ marginTop: 16, marginBottom: 4, color: fct.tinta['700'] }}>Observaciones</Paragraph>
          <Paragraph style={{ whiteSpace: 'pre-wrap' }}>{texto(d.observaciones)}</Paragraph>
        </>
      )}

      {d && editando && (
        <Form form={form} layout="vertical" requiredMark="optional">
          {venta && (
            <FormularioVenta
              form={form}
              catalogos={catalogos}
              edicion
              farmaciaInicial={venta.idFarmacia ? { value: venta.idFarmacia, label: venta.farmacia ?? `Farmacia ${venta.idFarmacia}` } : undefined}
              titularesIniciales={venta.titulares.map((t) => ({ value: t.idCliente, label: t.nombre ?? `Cliente ${t.idCliente}`, detalle: t.codigo }))}
              epcIniciales={{
                entidad: venta.idEpcEntidad ? { value: venta.idEpcEntidad, label: venta.epcEntidad ?? `#${venta.idEpcEntidad}` } : undefined,
                persona: venta.idEpcPersona ? { value: venta.idEpcPersona, label: venta.epcPersona ?? `#${venta.idEpcPersona}` } : undefined,
              }}
            />
          )}
          {compra && (
            <FormularioCompra
              form={form}
              catalogos={catalogos}
              edicion
              pestania={compra.pestania === Pestania.Solicitud ? Pestania.Solicitud : Pestania.Compra}
              clienteInicial={{ value: compra.idCliente, label: compra.cliente ?? `Cliente ${compra.idCliente}`, detalle: compra.codigoCliente }}
              operacionesIniciales={compra.operaciones.map((o) => ({ value: o.idOperacion, label: etiquetaOperacion(o), detalle: o.fase || undefined }))}
              epcIniciales={{
                entidad: compra.idEpcEntidad ? { value: compra.idEpcEntidad, label: compra.epcEntidad ?? `#${compra.idEpcEntidad}` } : undefined,
                persona: compra.idEpcPersona ? { value: compra.idEpcPersona, label: compra.epcPersona ?? `#${compra.idEpcPersona}` } : undefined,
              }}
            />
          )}
        </Form>
      )}
    </Drawer>
  );
};
